import { Keypair, Transaction } from '@stellar/stellar-sdk';

import type { VaultChallengeResponse } from 'api/vault';

import { VAULT_MARKER } from 'constants/vault';

type Signer = { key: string; weight: number; type?: string };

export const mergeChallengeSignatures = (original: Transaction, signedXdr: string): Transaction => {
    const signed = new Transaction(signedXdr, original.networkPassphrase);
    if (!Buffer.from(original.hash()).equals(Buffer.from(signed.hash()))) {
        throw new Error('The signed transaction does not match the authentication request.');
    }
    const unique = new Map(
        [...original.signatures, ...signed.signatures].map(signature => [
            signature.toXDR('base64'),
            signature,
        ]),
    );
    signed.signatures.splice(0, signed.signatures.length, ...unique.values());
    return signed;
};

export const getVaultChallengeProgress = (
    original: Transaction,
    response: VaultChallengeResponse,
    signers: Signer[],
    accountId: string,
    threshold: number,
) => {
    if (response.network_passphrase !== original.networkPassphrase) {
        throw new Error('Vault returned a transaction for a different network.');
    }
    const signed = mergeChallengeSignatures(original, response.xdr);
    const localWeight = signers.find(signer => signer.key === accountId)?.weight ?? 0;
    let weight = 0;
    let signedCount = 0;
    const coSigners = [...new Map(signers.map(value => [value.key, value])).values()].filter(
        signer =>
            signer.key !== accountId &&
            signer.key !== VAULT_MARKER &&
            signer.weight > 0 &&
            signer.key.startsWith('G'),
    );
    for (const signer of coSigners) {
        const key = Keypair.fromPublicKey(signer.key);
        if (
            signed.signatures.some(signature => {
                try {
                    return key.verify(signed.hash(), signature.signature.toBytes());
                } catch {
                    return false;
                }
            })
        ) {
            weight += signer.weight;
            signedCount += 1;
        }
    }
    const requiredWeight = Math.max(0, threshold - localWeight);
    const equalWeights =
        coSigners.length > 0 && coSigners.every(signer => signer.weight === coSigners[0].weight);
    return {
        signed: weight >= requiredWeight ? signed : null,
        signedCount,
        requiredCount:
            requiredWeight === 0
                ? 0
                : equalWeights
                  ? Math.ceil(requiredWeight / coSigners[0].weight)
                  : null,
        weight,
        requiredWeight,
    };
};

export const validateVaultChallenge = (
    ...args: Parameters<typeof getVaultChallengeProgress>
): Transaction | null => getVaultChallengeProgress(...args).signed;
